import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

const STATUS_LABEL_MAP = {
  sent: "Spedito",
  in_progress: "In compilazione",
  submitted: "Da verificare",
  rejected: "In correzione",
  pending_confirmation: "In ratifica",
  expired: "Scaduto",
  approved: "Valido",
  archived: "Archiviato",
};

const STATUS_CLASS_MAP = {
  Spedito: "tag-sent",
  "In compilazione": "tag-progress",
  "Da verificare": "tag-submitted",
  "In correzione": "tag-rejected",
  "In ratifica": "tag-ratifica",
  Scaduto: "tag-expired",
  Valido: "tag-approved",
  Archiviato: "tag-archived",
};

const AmlTable = ({
  data = [],
  onStatusInfoClick,
  emptyMessage = "Non ci sono aml in raccolta",
  columns = ["id", "stato", "cliente", "email", "data", "scadenza"],
  onRowClick,
}) => {
  const navigate = useNavigate();

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Typography className="color-blue-dark" style={{ fontWeight: 500 }}>
        {emptyMessage}
      </Typography>
    );
  }

  const renderHeaderCell = (col) => {
    const borderRight = "1px solid #fff";

    switch (col) {
      case "stato":
        return (
          <TableCell
            key={col}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              borderRight,
              width: "160px",
              whiteSpace: "nowrap",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography className="color-light title-column-table text-bold">
                Stato
              </Typography>
              {onStatusInfoClick && (
                <div
                  className="icon-modal-info-status"
                  style={{
                    marginLeft: "8px",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  onClick={onStatusInfoClick}
                />
              )}
            </div>
          </TableCell>
        );
      case "id":
        return (
          <TableCell
            key={col}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              borderRight,
              width: "80px",
              whiteSpace: "nowrap",
            }}
          >
            <Typography className="color-light title-column-table text-bold">
              Id
            </Typography>
          </TableCell>
        );
      case "cliente":
        return (
          <TableCell
            key={col}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              borderRight,
              whiteSpace: "nowrap",
              minWidth: "160px",
            }}
          >
            <Typography className="color-light title-column-table text-bold">
              Cliente
            </Typography>
          </TableCell>
        );
      case "email":
        return (
          <TableCell
            key={col}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              borderRight,
              whiteSpace: "nowrap",
              minWidth: "200px",
            }}
          >
            <Typography className="color-light title-column-table text-bold">
              Email
            </Typography>
          </TableCell>
        );
      case "data":
        return (
          <TableCell
            key={col}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              borderRight,
              width: "180px",
              whiteSpace: "nowrap",
            }}
          >
            <Typography className="color-light title-column-table text-bold">
              Data creazione
            </Typography>
          </TableCell>
        );
      case "scadenza":
        return (
          <TableCell
            key={col}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              borderRight,
              width: "180px",
              whiteSpace: "nowrap",
            }}
          >
            <Typography className="color-light title-column-table text-bold">
              Scadenza link
            </Typography>
          </TableCell>
        );
      case "approved_at":
        return (
          <TableCell
            key={col}
            sx={{
              fontWeight: 500,
              textAlign: "center",
              borderRight,
              width: "180px",
              whiteSpace: "nowrap",
            }}
          >
            <Typography className="color-light title-column-table text-bold">
              Data approvazione
            </Typography>
          </TableCell>
        );
      default:
        return null;
    }
  };

  const renderBodyCell = (col, item) => {
    const dateOnly = new Date(item.created_at).toLocaleDateString("it-IT");

    switch (col) {
      case "stato":
        const label = STATUS_LABEL_MAP[item.status] ?? item.status;
        return (
          <TableCell
            key={col}
            sx={{
              padding: "12px",
              width: "160px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
            className="color-blue-dark"
          >
            <Typography
              component="span"
              className={STATUS_CLASS_MAP[label] ?? null}
              sx={{
                fontWeight: 500,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mx: "auto",
                px: 2,
                py: 0.7,
                textAlign: "center",
                width: "100%",
                maxWidth: "160px",
                boxSizing: "border-box",
                fontSize: "14px",
                textTransform: "uppercase",
                borderRadius: "10px",
              }}
            >
              {label}
            </Typography>
          </TableCell>
        );
      case "id":
        return (
          <TableCell
            key={col}
            sx={{
              padding: "12px",
              width: "80px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              className="text-table color-purple"
              onClick={() => navigate(`/aml/${item.id}`)}
              sx={{
                cursor: "pointer",
                fontWeight: 500,
                textDecoration: "underline",
              }}
            >
              {item.id}
            </Typography>
          </TableCell>
        );
      case "cliente":
        return (
          <TableCell
            key={col}
            sx={{
              padding: "12px",
              textAlign: "center",
              whiteSpace: "nowrap",
              minWidth: "160px",
            }}
            className="color-blue-dark"
          >
            <Typography className="text-table">{item.name ?? "—"}</Typography>
          </TableCell>
        );
      case "email":
        return (
          <TableCell
            key={col}
            sx={{
              padding: "12px",
              textAlign: "center",
              whiteSpace: "nowrap",
              minWidth: "200px",
            }}
          >
            <Typography className="text-table color-blue-dark">
              {item.email}
            </Typography>
          </TableCell>
        );
      case "data":
        return (
          <TableCell
            key={col}
            sx={{
              padding: "12px",
              width: "180px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
            className="color-blue-dark"
          >
            <Typography className="text-table">{dateOnly}</Typography>
          </TableCell>
        );
      case "scadenza":
        const isExpired =
          item.link_expires_at &&
          new Date(item.link_expires_at) < new Date() &&
          item.status !== "archived";
        return (
          <TableCell
            key={col}
            sx={{
              padding: "12px",
              width: "180px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
            className="color-blue-dark"
          >
            <Typography className="text-table">
              {item.link_expires_at
                ? new Date(item.link_expires_at).toLocaleDateString("it-IT")
                : "—"}
            </Typography>
          </TableCell>
        );
      case "approved_at":
        return (
          <TableCell
            key={col}
            sx={{
              padding: "12px",
              width: "180px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
            className="color-blue-dark"
          >
            <Typography className="text-table">
              {item.approved_at
                ? new Date(item.approved_at).toLocaleDateString("it-IT")
                : "—"}
            </Typography>
          </TableCell>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        boxShadow:
          "-4px 0px 8px rgba(0,0,0,0.06), 4px 0px 8px rgba(0,0,0,0.06)",
        borderRadius: "16px",
        mt: "20px",
        mb: "34px",
      }}
    >
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "16px",
          mt: "20px",
          mb: "34px",
          width: "100%",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
          "&::-webkit-scrollbar": { height: "6px" },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c0c0c0",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: "#a0a0a0" },
        }}
      >
        <Table size="small" sx={{ minWidth: "760px" }}>
          <TableHead>
            <TableRow className="bg-blue-dark">
              {columns.map((col) => renderHeaderCell(col))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const isExpired =
                item.link_expires_at &&
                new Date(item.link_expires_at) < new Date() &&
                ["sent", "in_progress", "submitted", "rejected"].includes(
                  item.status,
                );

              return (
                <TableRow
                  key={index}
                  sx={{
                    height: "40px !important",
                    backgroundColor: isExpired ? "#fde8e8" : "transparent",
                  }}
                >
                  {columns.map((col) => renderBodyCell(col, item))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AmlTable;
